import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pagesAPI } from '../lib/api';
import { MapPin, Menu, X } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const PublicPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await pagesAPI.getPage(slug);
        setPage(res.data.page);
      } catch {
        setError('Page not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-xl text-gray-600">{error}</p>
      <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PageMeta title={page.meta_title || `${page.title} - GMB Autopilot`} description={page.meta_description || ''} />

      {/* Header - Same as Landing Page */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                GMB Autopilot
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors">Features</Link>
              <Link to="/pricing" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors">Pricing</Link>
              <Link to="/leaderboard" className="text-[#5F6368] hover:text-[#202124] font-medium transition-colors">Leaderboard</Link>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Link to="/dashboard" className="btn-google">Go to Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="text-[#5F6368] hover:text-[#202124] font-medium">Sign In</Link>
                  <Link to="/register" className="btn-google">Get Started Free</Link>
                </>
              )}
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#DADCE0] py-4 px-6">
            <nav className="flex flex-col gap-4">
              <Link to="/pricing" className="text-[#5F6368] hover:text-[#202124] font-medium">Pricing</Link>
              <Link to="/leaderboard" className="text-[#5F6368] hover:text-[#202124] font-medium">Leaderboard</Link>
              <hr className="border-[#DADCE0]" />
              {user ? (
                <Link to="/dashboard" className="btn-google text-center">Go to Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="btn-outline text-center">Sign In</Link>
                  <Link to="/register" className="btn-google text-center">Get Started Free</Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Page Content */}
      <main className="flex-1 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 py-12 lg:py-20">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E8EAED] p-8 lg:p-12">
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-semibold prose-headings:tracking-tight
                prose-h1:text-3xl prose-h1:text-[#202124] prose-h1:mb-6 prose-h1:pb-4 prose-h1:border-b prose-h1:border-[#E8EAED]
                prose-h2:text-xl prose-h2:text-[#202124] prose-h2:mt-8 prose-h2:mb-3
                prose-h3:text-lg prose-h3:text-[#374151]
                prose-p:text-[#5F6368] prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-[#1A73E8] prose-a:no-underline hover:prose-a:underline
                prose-li:text-[#5F6368] prose-li:leading-relaxed
                prose-ul:my-4 prose-ol:my-4
                prose-strong:text-[#202124]
                prose-blockquote:border-l-[#1A73E8] prose-blockquote:bg-[#F8F9FA] prose-blockquote:rounded-r-lg prose-blockquote:py-1"
              style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}
              dangerouslySetInnerHTML={{ __html: page.body }}
              data-testid="public-page-content"
            />
          </div>
          <p className="text-center text-sm text-[#9AA0A6] mt-6">Last updated: {new Date(page.updated_at || page.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </main>

      {/* Footer - Same as Landing Page */}
      <footer className="bg-[#202124] text-white py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-6 md:mb-0">
              <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>GMB Autopilot</span>
            </div>
            <div className="flex gap-8 text-sm text-[#9AA0A6]">
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/leaderboard" className="hover:text-white">Leaderboard</Link>
              <Link to="/login" className="hover:text-white">Sign In</Link>
            </div>
          </div>
          <div className="flex justify-center gap-6 mt-6 text-sm text-[#9AA0A6]">
            <Link to="/page/terms" className="hover:text-white">Terms & Conditions</Link>
            <Link to="/page/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/page/about" className="hover:text-white">About Us</Link>
          </div>
          <div className="border-t border-[#5F6368] mt-8 pt-6 text-center text-[#9AA0A6] text-sm">
            &copy; 2025 GMB Autopilot. Not affiliated with Google. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicPage;
